import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_SECTION,
  DEFAULT_SUB,
  SECTIONS,
  Section,
  SectionId,
  SubStep,
} from "../components/stepper/steps";

type FlatStep = {
  section: Section;
  sub: SubStep;
  sectionIndex: number;
  subIndex: number;
  globalIndex: number;
};

const flattenSections = (sections: Section[]): FlatStep[] => {
  const flat: FlatStep[] = [];
  let g = 0;
  sections.forEach((section, sectionIndex) => {
    section.subs.forEach((sub, subIndex) => {
      flat.push({ section, sub, sectionIndex, subIndex, globalIndex: g++ });
    });
  });
  return flat;
};

export type StepperState = {
  sections: Section[];
  current: FlatStep;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  // True before the user has entered the stepper flow (no ?section= in URL).
  // When true, the consultation should render the splash and hide the stepper.
  isSplash: boolean;
  goTo: (sectionId: SectionId, subId: string) => void;
  goToSection: (sectionId: SectionId) => void;
  goToSub: (subId: string) => void;
  goNext: () => void;
  goPrev: () => void;
  // Leave the splash by entering the first step of Section 1.
  enterStepper: () => void;
};

/**
 * Stepper state synced to URL search params:
 *   ?section=<sectionId>&sub=<subId>
 *
 * Pass a filtered `sections` list (e.g. from useAvailableSections) to skip
 * sub-steps that aren't applicable to the current client (e.g. body-composition
 * when no InBody data).
 */
const useStepperNavigation = (
  sections: Section[] = SECTIONS
): StepperState => {
  const [searchParams, setSearchParams] = useSearchParams();

  const flat = useMemo(() => flattenSections(sections), [sections]);

  // Splash mode: no ?section= in URL. The consultation lands here first,
  // shows the AI/Self Guided panel, and proceeds into the stepper on click.
  const isSplash = !searchParams.get("section");

  // All lookups resolve against the FILTERED `sections` (not the static
  // SECTIONS) so a sub-step that was filtered out for this client (e.g. an
  // unapproved system) is never targeted — otherwise `flat.find` misses and
  // navigation falls back to `flat[0]`, kicking the user to the start.
  const current = useMemo<FlatStep>(() => {
    const sectionParam = searchParams.get("section");
    const subParam = searchParams.get("sub");
    const section =
      sections.find((s) => s.id === sectionParam) ?? sections[0];
    const sub =
      section.subs.find((s) => s.id === subParam) ?? section.subs[0];
    const found =
      flat.find(
        (f) => f.section.id === section.id && f.sub.id === sub.id
      ) ?? flat[0];
    return found;
  }, [searchParams, sections, flat]);

  const writeParams = useCallback(
    (sectionId: SectionId, subId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set("section", sectionId);
      next.set("sub", subId);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const goTo = useCallback(
    (sectionId: SectionId, subId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      const sub = section?.subs.find((s) => s.id === subId);
      if (section && sub) writeParams(section.id, sub.id);
    },
    [sections, writeParams]
  );

  const goToSection = useCallback(
    (sectionId: SectionId) => {
      const section = sections.find((s) => s.id === sectionId);
      // Land on the first AVAILABLE sub-step of the target section. Guard
      // against an empty section (all subs filtered out) so we never write a
      // `sub` that isn't in `flat`.
      if (section && section.subs.length > 0) {
        writeParams(section.id, section.subs[0].id);
      }
    },
    [sections, writeParams]
  );

  const goToSub = useCallback(
    (subId: string) => {
      const sub = current.section.subs.find((s) => s.id === subId);
      if (sub) writeParams(current.section.id, sub.id);
    },
    [current.section, writeParams]
  );

  const goNext = useCallback(() => {
    const next = flat[current.globalIndex + 1];
    if (next) writeParams(next.section.id, next.sub.id);
  }, [current.globalIndex, flat, writeParams]);

  const goPrev = useCallback(() => {
    const prev = flat[current.globalIndex - 1];
    if (prev) writeParams(prev.section.id, prev.sub.id);
  }, [current.globalIndex, flat, writeParams]);

  const enterStepper = useCallback(() => {
    const first = flat[0];
    if (first) writeParams(first.section.id, first.sub.id);
  }, [flat, writeParams]);

  return {
    sections,
    current,
    totalSteps: flat.length,
    isFirst: current.globalIndex === 0,
    isLast: current.globalIndex === flat.length - 1,
    isSplash,
    goTo,
    goToSection,
    goToSub,
    goNext,
    goPrev,
    enterStepper,
  };
};

export default useStepperNavigation;
export const __DEFAULTS__ = { DEFAULT_SECTION, DEFAULT_SUB };
